import { z } from 'zod';
import {
  DEFAULT_VAT_RATE_BPS,
  FEATURE_KEYS,
  MAX_VAT_RATE_BPS,
  PLAN_KEYS,
  RESOURCES,
} from '../plans';
import { UuidSchema } from './common';

export const SUBSCRIPTION_STATUSES = [
  'incomplete',
  'incomplete_expired',
  'trialing',
  'active',
  'past_due',
  'canceled',
  'unpaid',
  'paused',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const STRIPE_EVENT_STATUSES = ['received', 'processing', 'processed', 'failed'] as const;
export type StripeEventStatus = (typeof STRIPE_EVENT_STATUSES)[number];

export const PURCHASE_STATUSES = [
  'pending',
  'paid',
  'granted',
  'failed',
  'refunded',
  'expired',
] as const;
export type PurchaseStatus = (typeof PURCHASE_STATUSES)[number];

/** Where credits came from. Consumption order: subscription → gift → adjustment → refund → topup. */
export const GRANT_SOURCES = ['subscription', 'topup', 'gift', 'adjustment', 'refund'] as const;
export type GrantSource = (typeof GRANT_SOURCES)[number];

export const LEDGER_ENTRY_TYPES = [
  'grant',
  'reserve',
  'capture',
  'release',
  'expire',
  'reverse',
  'adjust',
] as const;
export type LedgerEntryType = (typeof LEDGER_ENTRY_TYPES)[number];

export const RESERVATION_STATUSES = ['active', 'settled', 'released', 'expired'] as const;
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];

export const USAGE_STATUSES = ['reserved', 'captured', 'released', 'failed'] as const;
export type UsageStatus = (typeof USAGE_STATUSES)[number];

export const ResourceSchema = z.enum(RESOURCES);

/**
 * VAT rate in basis points (2400 = 24%). Catalog prices are net; checkout adds this rate
 * (ADR-B10). Defaults to the seed rate for payloads from an older API (version skew).
 */
export const VatRateBpsSchema = z
  .number()
  .int()
  .min(0)
  .max(MAX_VAT_RATE_BPS)
  .default(DEFAULT_VAT_RATE_BPS);

export const PlanSchema = z.object({
  key: z.enum(PLAN_KEYS),
  name: z.string(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string(),
  interval: z.literal('month'),
  stripePriceId: z.string().nullable(),
  allowances: z.record(ResourceSchema, z.number().int().nonnegative()),
  features: z.array(z.enum(FEATURE_KEYS)),
  limits: z.record(z.string(), z.unknown()),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type Plan = z.infer<typeof PlanSchema>;

export const TopupBundleSchema = z.object({
  key: z.string(),
  name: z.string(),
  resource: ResourceSchema,
  quantity: z.number().int().positive(),
  priceCents: z.number().int().nonnegative(),
  currency: z.string(),
  availableOnPlans: z.array(z.enum(PLAN_KEYS)),
  stripePriceId: z.string().nullable(),
  active: z.boolean(),
});
export type TopupBundle = z.infer<typeof TopupBundleSchema>;

export const PlansResponseSchema = z.object({
  plans: z.array(PlanSchema),
  bundles: z.array(TopupBundleSchema),
  /** Payment mode of the deployment: `fake` shows the test-payment page instead of Stripe. */
  mode: z.enum(['fake', 'test', 'live']),
  /** Validity of purchased top-up credits (billing config `billing.topupValidityDays`). */
  topupValidityDays: z.number().int().positive().optional(),
  /** VAT added on top of every (net) plan and bundle price at checkout. */
  vatRateBps: VatRateBpsSchema,
});
export type PlansResponse = z.infer<typeof PlansResponseSchema>;

export const CheckoutRequestSchema = z.object({ planKey: z.enum(PLAN_KEYS) });
export const TopupCheckoutRequestSchema = z.object({ bundleKey: z.string().min(1).max(64) });
export const CheckoutResponseSchema = z.object({ url: z.url(), sessionId: z.string() });
export type CheckoutResponse = z.infer<typeof CheckoutResponseSchema>;
export const PortalResponseSchema = z.object({ url: z.url() });
export const ChangePlanRequestSchema = z.object({ planKey: z.enum(PLAN_KEYS) });
export const ChangePlanResponseSchema = z.object({
  status: z.enum(['applied', 'pending', 'unchanged']),
  planKey: z.enum(PLAN_KEYS),
});
export type ChangePlanResponse = z.infer<typeof ChangePlanResponseSchema>;
export const ChangePlanPreviewQuerySchema = z.object({ planKey: z.enum(PLAN_KEYS) });
/** GET /billing/change-plan/preview: what confirming the change charges today. */
export const ChangePlanPreviewSchema = z.object({
  planKey: z.enum(PLAN_KEYS),
  /** Total charged today, VAT included. */
  amountDueCents: z.number().int().nonnegative(),
  /** VAT part of `amountDueCents` (net = amountDueCents - vatCents). */
  vatCents: z.number().int().nonnegative().default(0),
  vatRateBps: VatRateBpsSchema,
  currency: z.string(),
  mode: z.enum(['fake', 'test', 'live']),
  /** `immediate`: applied on confirm (fake); `on_payment`: once Stripe collects the charge. */
  effective: z.enum(['immediate', 'on_payment']),
});
export type ChangePlanPreview = z.infer<typeof ChangePlanPreviewSchema>;

/** Why spending (sending, paid AI, top-ups) is blocked — null when allowed. */
export const SPENDING_BLOCK_REASONS = [
  'pending_subscription',
  'past_due_grace_over',
  'canceled',
  'suspended',
  'terminated',
] as const;
export type SpendingBlockReason = (typeof SPENDING_BLOCK_REASONS)[number];

export const SpendingPolicySchema = z.object({
  allowed: z.boolean(),
  reason: z.enum(SPENDING_BLOCK_REASONS).nullable(),
  /** past_due only: until when sending keeps working. */
  graceEndsAt: z.string().nullable(),
});
export type SpendingPolicy = z.infer<typeof SpendingPolicySchema>;

export const InvoiceSchema = z.object({
  id: UuidSchema,
  stripeInvoiceId: z.string(),
  status: z.string(),
  amountDueCents: z.number().int(),
  amountPaidCents: z.number().int(),
  refundedCents: z.number().int(),
  currency: z.string(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  billingReason: z.string().nullable(),
  paidAt: z.string().nullable(),
  hostedUrl: z.string().nullable(),
  createdAt: z.string(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

export const SubscriptionResponseSchema = z.object({
  mode: z.enum(['fake', 'test', 'live']),
  tenantStatus: z.string(),
  plan: PlanSchema.nullable(),
  subscription: z
    .object({
      status: z.enum(SUBSCRIPTION_STATUSES),
      planKey: z.enum(PLAN_KEYS).nullable(),
      currentPeriodStart: z.string().nullable(),
      currentPeriodEnd: z.string().nullable(),
      cancelAtPeriodEnd: z.boolean(),
      canceledAt: z.string().nullable(),
      pastDueSince: z.string().nullable(),
      latestInvoiceStatus: z.string().nullable(),
      priceCents: z.number().int().nullable(),
      currency: z.string().nullable(),
      hasCustomer: z.boolean(),
      /**
       * VAT rate this subscription is charged (its Stripe default tax rate; 0 = none). Can differ
       * from the platform `vatRateBps` until Super Admin applies a new rate (ADR-B10).
       */
      vatRateBps: z.number().int().min(0).optional(),
    })
    .nullable(),
  spending: SpendingPolicySchema,
  /** Invoice amounts are what Stripe charged, VAT included. */
  invoices: z.array(InvoiceSchema),
  /** Current VAT rate (plan prices above are net). */
  vatRateBps: VatRateBpsSchema,
});
export type SubscriptionResponse = z.infer<typeof SubscriptionResponseSchema>;

export const GrantViewSchema = z.object({
  id: UuidSchema,
  source: z.enum(GRANT_SOURCES),
  originalQty: z.number().int(),
  remainingQty: z.number().int(),
  expiresAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
});
export type GrantView = z.infer<typeof GrantViewSchema>;

export const WalletDetailSchema = z.object({
  resource: ResourceSchema,
  available: z.number().int(),
  reserved: z.number().int(),
  consumed: z.number().int(),
  expired: z.number().int(),
  reversed: z.number().int(),
  totalGranted: z.number().int(),
  /** Monthly allowance of the current plan. */
  allowance: z.number().int(),
  /** Remaining (unreserved) units per source, in consumption order. */
  bySource: z.object({
    subscription: z.number().int(),
    gift: z.number().int(),
    adjustment: z.number().int(),
    refund: z.number().int(),
    topup: z.number().int(),
  }),
  /** Earliest expiry among grants with remaining units. */
  nextExpiry: z.object({ at: z.string(), quantity: z.number().int() }).nullable(),
  /** Grants that still have units, in consumption order. */
  grants: z.array(GrantViewSchema),
});
export type WalletDetail = z.infer<typeof WalletDetailSchema>;
export const WalletsResponseSchema = z.object({ wallets: z.array(WalletDetailSchema) });
export type WalletsResponse = z.infer<typeof WalletsResponseSchema>;

export const LedgerEntrySchema = z.object({
  id: UuidSchema,
  resource: ResourceSchema,
  entryType: z.enum(LEDGER_ENTRY_TYPES),
  quantity: z.number().int(),
  operationId: z.string().nullable(),
  grantSource: z.enum(GRANT_SOURCES).nullable(),
  actor: z.string().nullable(),
  reason: z.string().nullable(),
  availableAfter: z.number().int(),
  createdAt: z.string(),
});
export type LedgerEntry = z.infer<typeof LedgerEntrySchema>;

export const LedgerQuerySchema = z.object({
  resource: ResourceSchema.optional(),
  entryType: z.enum(LEDGER_ENTRY_TYPES).optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export const LedgerResponseSchema = z.object({
  items: z.array(LedgerEntrySchema),
  nextCursor: z.string().nullable(),
});
export type LedgerResponse = z.infer<typeof LedgerResponseSchema>;

export const UsageQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(90).default(30),
  resource: ResourceSchema.optional(),
});
export const UsageResponseSchema = z.object({
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  /** One row per calendar day (tenant timezone), zero-filled. */
  byDay: z.array(
    z.object({
      date: z.string(),
      sms: z.number().int(),
      email: z.number().int(),
      ai: z.number().int(),
    }),
  ),
  byPurpose: z.array(
    z.object({ purpose: z.string(), resource: ResourceSchema, quantity: z.number().int() }),
  ),
  totals: z.object({ sms: z.number().int(), email: z.number().int(), ai: z.number().int() }),
});
export type UsageResponse = z.infer<typeof UsageResponseSchema>;

/**
 * Display key for a credit/usage purpose. AI spend is reserved as `ai.<purpose>` (e.g.
 * `ai.general_chat`) while the labels are keyed by the bare purpose, so the prefix is dropped.
 * Missing reasons become `other`. The SQL twin is `regexp_replace(reason, '^ai[.]', '')`.
 */
export function usagePurposeKey(reason: string | null | undefined): string {
  if (!reason) return 'other';
  return reason.startsWith('ai.') ? reason.slice(3) || 'other' : reason;
}

export const PurchaseSchema = z.object({
  id: UuidSchema,
  bundleKey: z.string(),
  resource: ResourceSchema,
  quantity: z.number().int(),
  status: z.enum(PURCHASE_STATUSES),
  /** What the checkout charged, VAT included (refunds are proportional to it). */
  amountCents: z.number().int(),
  currency: z.string(),
  refundedAmountCents: z.number().int(),
  /** Stripe's hosted invoice of the top-up (VAT shown), when one was created. */
  invoiceUrl: z.string().nullable().default(null),
  grantedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Purchase = z.infer<typeof PurchaseSchema>;
export const PurchasesResponseSchema = z.object({ items: z.array(PurchaseSchema) });
export const InvoicesResponseSchema = z.object({ items: z.array(InvoiceSchema) });

/** Dev-only fake checkout (STRIPE_MODE=fake). */
export const FakeCheckoutSessionSchema = z.object({
  sessionId: z.string(),
  kind: z.enum(['subscription', 'topup']),
  /** `expired`: superseded by a newer checkout of the same tenant (cannot be paid). */
  status: z.enum(['open', 'complete', 'failed', 'expired']),
  tenantName: z.string().nullable(),
  title: z.string(),
  planKey: z.enum(PLAN_KEYS).nullable(),
  bundleKey: z.string().nullable(),
  /** Total to pay, VAT included (= netCents + vatCents). */
  amountCents: z.number().int(),
  /** Catalog (net) price. */
  netCents: z.number().int(),
  vatCents: z.number().int(),
  vatRateBps: VatRateBpsSchema,
  currency: z.string(),
  successUrl: z.string(),
  cancelUrl: z.string(),
});
export type FakeCheckoutSession = z.infer<typeof FakeCheckoutSessionSchema>;
export const FakeCheckoutCompleteRequestSchema = z.object({ outcome: z.enum(['pay', 'fail']) });
export const FakeCheckoutCompleteResponseSchema = z.object({
  status: z.enum(['complete', 'failed']),
  redirectUrl: z.string(),
  events: z.array(z.string()),
});
export const DevAdvanceCycleRequestSchema = z.object({
  outcome: z.enum(['pay', 'fail']).default('pay'),
});
export const DevEventsResponseSchema = z.object({ events: z.array(z.string()) });
